import {
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Spinner,
} from '@crm/ui';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import { Link, useSearchParams } from 'react-router-dom';
import { FormError, FormField, FormSuccess } from '@/components';
import { useDocumentTitle } from '@/hooks';
import { AUTH_ROUTES } from '../constants';
import { useResetPassword } from '../hooks';
import { type ResetPasswordFormValues, resetPasswordSchema } from '../schemas';
import { toErrorMessage } from '../utils';

export function ResetPasswordPage() {
  useDocumentTitle('Nova senha');

  const [searchParams] = useSearchParams();
  const token = searchParams.get('token') ?? '';
  const resetPassword = useResetPassword();

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<ResetPasswordFormValues>({
    resolver: zodResolver(resetPasswordSchema),
    defaultValues: { password: '', confirmPassword: '' },
  });

  return (
    <Card className="w-full max-w-md">
      <CardHeader>
        <CardTitle className="text-xl">Definir nova senha</CardTitle>
        <CardDescription>Escolha uma senha de no minimo 8 caracteres.</CardDescription>
      </CardHeader>
      <CardContent>
        {!token ? (
          <div className="space-y-4">
            <FormError message="Link invalido: o token de redefinicao nao veio na URL." />
            <Button variant="outline" className="w-full" asChild>
              <Link to={AUTH_ROUTES.FORGOT_PASSWORD}>Solicitar um novo link</Link>
            </Button>
          </div>
        ) : resetPassword.isSuccess ? (
          <div className="space-y-4">
            <FormSuccess message="Senha redefinida. Ja pode entrar com a nova senha." />
            <Button className="w-full" asChild>
              <Link to={AUTH_ROUTES.LOGIN}>Ir para o login</Link>
            </Button>
          </div>
        ) : (
          <form
            className="space-y-4"
            onSubmit={handleSubmit((values) =>
              resetPassword.mutate({ token, password: values.password }),
            )}
            noValidate
          >
            <FormError message={resetPassword.error ? toErrorMessage(resetPassword.error) : null} />

            <FormField
              label="Nova senha"
              type="password"
              autoComplete="new-password"
              placeholder="********"
              error={errors.password?.message}
              {...register('password')}
            />

            <FormField
              label="Confirmar nova senha"
              type="password"
              autoComplete="new-password"
              placeholder="********"
              error={errors.confirmPassword?.message}
              {...register('confirmPassword')}
            />

            <Button type="submit" className="w-full" disabled={resetPassword.isPending}>
              {resetPassword.isPending ? <Spinner /> : null}
              Salvar nova senha
            </Button>
          </form>
        )}
      </CardContent>
    </Card>
  );
}
